import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { UserPlus, Edit2, Trash2, X, CheckCircle, AlertCircle, RotateCcw, Wallet } from 'lucide-react'

import { apiClient } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { useOrganization } from '../contexts/OrganizationContext'

interface User {
  id: string
  email: string
  display_name: string | null
  phone: string | null
  role: string
  status: string
  is_active: boolean
  can_review_invoices: boolean
  can_view_reports: boolean
  can_order_ambulance: boolean
  can_review_ambulance: boolean
  last_login_at: string | null
  invited_at: string | null
  created_at: string
}

interface UserInvite {
  email: string
  display_name: string
  phone: string
  role: string
  can_review_invoices: boolean
  can_view_reports: boolean
  can_order_ambulance: boolean
  can_review_ambulance: boolean
  organization_id?: string
}

interface CurrentUserInfo {
  role?: string
  is_system_admin_org?: boolean
  organization_settings?: {
    features?: {
      ambulance_orders_enabled?: boolean
    }
  }
}

interface DomainCheckResponse {
  valid: boolean
  organization_id: string | null
  organization_name: string | null
  message: string | null
  error_code: string | null
  domain: string | null
}

interface UserUpdate {
  role?: string
  phone?: string
  can_review_invoices?: boolean
  can_view_reports?: boolean
  can_order_ambulance?: boolean
  can_review_ambulance?: boolean
}

interface CostCenter {
  id: string
  code: string
  name: string
  is_deleted: boolean
}

interface CostCenterListResponse {
  data: CostCenter[]
  total: number
  page: number
  page_size: number
}

interface CostCenterPermission {
  id: string
  user_id: string
  cost_center_id: string
  valid_from: string
  valid_to: string | null
  is_active: boolean
  cost_center_code?: string | null
  cost_center_name?: string | null
}

interface PermissionEntry {
  checked: boolean
  valid_from: string
  valid_to: string
  permissionId?: string
  originalValidFrom?: string
  originalValidTo?: string
}

export default function UsersPage() {
  const { t } = useTranslation('admin')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()
  const { selectedOrgId } = useOrganization()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [showInactive, setShowInactive] = useState(false)
  const [permissionModalUser, setPermissionModalUser] = useState<User | null>(null)
  const [permissionMap, setPermissionMap] = useState<Record<string, PermissionEntry>>({})
  const [permissionSearch, setPermissionSearch] = useState('')
  const [isSavingPermissions, setIsSavingPermissions] = useState(false)
  const permissionMapInitialized = useRef(false)
  const [domainCheck, setDomainCheck] = useState<DomainCheckResponse | null>(null)
  const [newUser, setNewUser] = useState<UserInvite>({
    email: '',
    display_name: '',
    phone: '',
    role: 'user',
    can_review_invoices: false,
    can_view_reports: false,
    can_order_ambulance: false,
    can_review_ambulance: false,
  })
  const [sendInviteEmail, setSendInviteEmail] = useState(false)

  const { data, isLoading, error } = useQuery<{ data: User[] }>({
    queryKey: ['users', selectedOrgId, showInactive],
    queryFn: () => {
      const params = new URLSearchParams()
      if (selectedOrgId) params.set('organization_id', selectedOrgId)
      if (showInactive) params.set('include_inactive', 'true')
      const qs = params.toString()
      return apiClient.get(`/users${qs ? `?${qs}` : ''}`)
    },
  })

  // Get current user to check if ambulance feature is enabled
  const { data: currentUserInfo } = useQuery<CurrentUserInfo>({
    queryKey: ['currentUser'],
    queryFn: () => apiClient.get('/auth/me'),
    staleTime: 5 * 60 * 1000,
  })

  // Check if current user is system admin
  const isSystemAdmin = currentUserInfo?.role === 'system_admin' ||
    (currentUserInfo?.role === 'organization_admin' && currentUserInfo?.is_system_admin_org)

  // Fetch selected organization's settings when sys_admin changes org
  const { data: selectedOrgData } = useQuery<{ settings?: { features?: { ambulance_orders_enabled?: boolean } } }>({
    queryKey: ['organization', selectedOrgId],
    queryFn: () => apiClient.get(`/organizations/${selectedOrgId}`),
    enabled: isSystemAdmin && !!selectedOrgId,
    staleTime: 5 * 60 * 1000,
  })

  // Use selected organization's settings if sys_admin has switched org
  const effectiveOrgSettings = (isSystemAdmin && selectedOrgId && selectedOrgData)
    ? selectedOrgData.settings
    : currentUserInfo?.organization_settings

  // Ambulance permissions only shown if organization has feature enabled
  const ambulanceEnabled = effectiveOrgSettings?.features?.ambulance_orders_enabled === true

  // Cost centers for permission modal
  const { data: costCentersData } = useQuery<CostCenterListResponse>({
    queryKey: ['costCentersAll', selectedOrgId],
    queryFn: () => {
      const params = new URLSearchParams({ page_size: '10000' })
      if (selectedOrgId) params.set('organization_id', selectedOrgId)
      return apiClient.get(`/cost-centers?${params.toString()}`)
    },
    enabled: !!permissionModalUser,
    staleTime: 60 * 1000,
  })

  // Existing permissions for selected user
  const { data: userPermsData } = useQuery<CostCenterPermission[]>({
    queryKey: ['userCostCenterPerms', permissionModalUser?.id, selectedOrgId],
    queryFn: () => {
      const params = new URLSearchParams()
      if (selectedOrgId) params.set('organization_id', selectedOrgId)
      const qs = params.toString()
      return apiClient.get(`/users/${permissionModalUser!.id}/cost-center-permissions${qs ? `?${qs}` : ''}`)
    },
    enabled: !!permissionModalUser,
  })

  // Reset initialization flag when modal user changes
  useEffect(() => {
    permissionMapInitialized.current = false
    if (!permissionModalUser) {
      setPermissionMap({})
      setPermissionSearch('')
    }
  }, [permissionModalUser?.id])

  // Initialize permission map when both data sources are ready
  useEffect(() => {
    if (!permissionModalUser || permissionMapInitialized.current) return
    if (!costCentersData || !userPermsData) return

    permissionMapInitialized.current = true
    const today = new Date().toISOString().split('T')[0]
    const permByCC: Record<string, CostCenterPermission> = {}
    for (const p of userPermsData) {
      permByCC[p.cost_center_id] = p
    }

    const map: Record<string, PermissionEntry> = {}
    for (const cc of costCentersData.data) {
      if (cc.is_deleted) continue
      const existing = permByCC[cc.id]
      if (existing) {
        map[cc.id] = {
          checked: true,
          valid_from: existing.valid_from,
          valid_to: existing.valid_to || '',
          permissionId: existing.id,
          originalValidFrom: existing.valid_from,
          originalValidTo: existing.valid_to || '',
        }
      } else {
        map[cc.id] = {
          checked: false,
          valid_from: today,
          valid_to: '',
        }
      }
    }
    setPermissionMap(map)
  }, [costCentersData, userPermsData, permissionModalUser])

  const inviteUserMutation = useMutation({
    mutationFn: (userData: UserInvite) => {
      // Include organization_id when sys_admin has selected a different org
      const payload = selectedOrgId
        ? { ...userData, organization_id: selectedOrgId }
        : userData
      return apiClient.post('/users/invite', payload)
    },
    onSuccess: () => {
      // Open mailto if checkbox was checked
      if (sendInviteEmail && newUser.email) {
        const appUrl = window.location.origin
        const subject = encodeURIComponent('Kutsu Taksi Helsingin Tilaajakanavaan')
        const body = encodeURIComponent(`Hei${newUser.display_name ? ' ' + newUser.display_name : ''},

Sinut on kutsuttu Taksi Helsingin Tilaajakanava-palveluun.

Kirjaudu sisään Microsoft-tunnuksillasi:
${appUrl}

Ystävällisin terveisin`)
        window.location.href = `mailto:${newUser.email}?subject=${subject}&body=${body}`
      }
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setIsModalOpen(false)
      setNewUser({ email: '', display_name: '', phone: '', role: 'user', can_review_invoices: false, can_view_reports: false, can_order_ambulance: false, can_review_ambulance: false })
      setDomainCheck(null)
      setSendInviteEmail(false)
    },
  })

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UserUpdate }) => 
      apiClient.patch(`/users/${userId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setIsEditModalOpen(false)
      setEditingUser(null)
    },
  })

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => apiClient.delete(`/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const restoreUserMutation = useMutation({
    mutationFn: (userId: string) => apiClient.post(`/users/${userId}/restore`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const handleDeleteUser = (user: User) => {
    if (window.confirm(`${t('users.confirmDelete') || 'Haluatko varmasti poistaa käyttäjän'} ${user.email}?`)) {
      deleteUserMutation.mutate(user.id)
    }
  }

  const handleRestoreUser = (user: User) => {
    if (window.confirm(`${t('users.confirmRestore')} ${user.email}?`)) {
      restoreUserMutation.mutate(user.id)
    }
  }

  const handleOpenPermissionModal = (user: User) => {
    setPermissionModalUser(user)
  }

  const handleClosePermissionModal = () => {
    setPermissionModalUser(null)
  }

  const handleSavePermissions = async () => {
    if (!permissionModalUser) return
    setIsSavingPermissions(true)
    try {
      const today = new Date().toISOString().split('T')[0]
      const orgParam = selectedOrgId ? `?organization_id=${selectedOrgId}` : ''
      const promises: Promise<unknown>[] = []

      for (const [ccId, entry] of Object.entries(permissionMap)) {
        if (entry.checked && !entry.permissionId) {
          // Grant new permission
          promises.push(
            apiClient.post(`/cost-centers/${ccId}/permissions${orgParam}`, {
              user_id: permissionModalUser.id,
              valid_from: entry.valid_from || today,
              valid_to: entry.valid_to || null,
            }),
          )
        } else if (!entry.checked && entry.permissionId) {
          // Revoke existing permission
          promises.push(
            apiClient.delete(`/cost-centers/${ccId}/permissions/${entry.permissionId}`),
          )
        } else if (entry.checked && entry.permissionId) {
          // Update if dates changed
          const fromChanged = entry.valid_from !== entry.originalValidFrom
          const toChanged = entry.valid_to !== (entry.originalValidTo || '')
          if (fromChanged || toChanged) {
            promises.push(
              apiClient.patch(`/cost-centers/${ccId}/permissions/${entry.permissionId}`, {
                valid_from: entry.valid_from || today,
                valid_to: entry.valid_to || null,
              }),
            )
          }
        }
      }

      await Promise.all(promises)
      queryClient.invalidateQueries({ queryKey: ['userCostCenterPerms'] })
      handleClosePermissionModal()
    } finally {
      setIsSavingPermissions(false)
    }
  }

  const handleEditUser = (user: User) => {
    setEditingUser(user)
    setIsEditModalOpen(true)
  }

  const handleUpdateUser = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingUser) return
    updateUserMutation.mutate({
      userId: editingUser.id,
      data: {
        role: editingUser.role,
        phone: editingUser.phone || undefined,
        can_review_invoices: editingUser.can_review_invoices,
        can_view_reports: editingUser.can_view_reports,
        can_order_ambulance: editingUser.can_order_ambulance,
        can_review_ambulance: editingUser.can_review_ambulance,
      },
    })
  }

  // Check domain when email changes
  const checkDomain = async (email: string) => {
    if (!email.includes('@')) {
      setDomainCheck(null)
      return
    }
    try {
      const result = await apiClient.post<DomainCheckResponse>('/organizations/check-domain', { email })
      setDomainCheck(result)
    } catch {
      setDomainCheck({ 
        valid: false, 
        organization_id: null, 
        organization_name: null, 
        message: 'Domain check failed',
        error_code: 'ERROR',
        domain: null,
      })
    }
  }

  const handleEmailChange = (email: string) => {
    setNewUser({ ...newUser, email })
    // Clear domain check when typing
    setDomainCheck(null)
  }

  const handleEmailBlur = (email: string) => {
    // Check domain only when leaving the field
    if (email.includes('@')) {
      checkDomain(email)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    inviteUserMutation.mutate(newUser)
  }

  const statusColors: Record<string, string> = {
    invited: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    deactivated: 'bg-gray-100 text-gray-800',
  }

  const statusLabels: Record<string, string> = {
    invited: t('users.statusInvited') || 'Kutsuttu',
    active: t('users.statusActive') || 'Aktiivinen',
    deactivated: t('users.statusDeactivated') || 'Deaktivoitu',
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{tc('errors.generic')}</p>
      </div>
    )
  }

  const users = data?.data || []

  const roleLabels: Record<string, string> = {
    system_admin: tc('roles.system_admin'),
    organization_admin: tc('roles.organization_admin'),
    user: tc('roles.user'),
    invoice_reviewer: tc('roles.user'),
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('users.title')}</h1>
          <p className="mt-1 text-gray-600">{t('users.subtitle')}</p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            {t('users.showDeleted')}
          </label>
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            {t('users.addUser')}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.email')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.displayName')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.role')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.lastLogin')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('users.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    {t('users.noUsers')}
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className={`hover:bg-gray-50 ${!user.is_active ? 'opacity-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user.display_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {roleLabels[user.role] || user.role}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          statusColors[user.status] || 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {statusLabels[user.status] || user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user.last_login_at
                        ? new Date(user.last_login_at).toLocaleString()
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      {user.is_active ? (
                        <>
                          <button
                            onClick={() => handleOpenPermissionModal(user)}
                            className="text-gray-400 hover:text-primary-600 mr-3"
                            title={t('users.manageCostCenterPermissions')}
                          >
                            <Wallet className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditUser(user)}
                            className="text-gray-400 hover:text-primary-600 mr-3"
                            title={t('users.editUser')}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="text-gray-400 hover:text-red-600"
                            title={t('users.deleteUser')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleRestoreUser(user)}
                          className="text-gray-400 hover:text-green-600"
                          title={t('users.restoreUser')}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">{t('users.addUser')}</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.email')} *
                </label>
                <input
                  type="email"
                  required
                  value={newUser.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={(e) => handleEmailBlur(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                {/* Domain check result */}
                {domainCheck && (
                  <div className={`mt-2 flex items-center gap-2 text-sm ${domainCheck.valid ? 'text-green-600' : 'text-red-600'}`}>
                    {domainCheck.valid ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span>{t('users.domainCheck.valid', { name: domainCheck.organization_name })}</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4" />
                        <span>
                          {domainCheck.error_code === 'INVALID_EMAIL' && t('users.domainCheck.invalidEmail')}
                          {domainCheck.error_code === 'INVALID_DOMAIN' && t('users.domainCheck.invalidDomain', { domain: domainCheck.domain })}
                          {domainCheck.error_code === 'NOT_FOUND' && t('users.domainCheck.notFound')}
                          {domainCheck.error_code === 'ERROR' && t('users.domainCheck.error')}
                          {!domainCheck.error_code && domainCheck.message}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.displayName')}
                </label>
                <input
                  type="text"
                  value={newUser.display_name}
                  onChange={(e) => setNewUser({ ...newUser, display_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.phone')}
                </label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.role')} *
                </label>
                <select
                  required
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="user">{tc('roles.user')}</option>
                  <option value="organization_admin">{tc('roles.organization_admin')}</option>
                </select>
              </div>
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  {t('users.permissions')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="can_review_invoices"
                    checked={newUser.can_review_invoices}
                    onChange={(e) => setNewUser({ ...newUser, can_review_invoices: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="can_review_invoices" className="text-sm text-gray-700">
                    {t('users.canReviewInvoices')}
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="can_view_reports"
                    checked={newUser.can_view_reports}
                    onChange={(e) => setNewUser({ ...newUser, can_view_reports: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="can_view_reports" className="text-sm text-gray-700">
                    {t('users.canViewReports')}
                  </label>
                </div>
                {ambulanceEnabled && (
                  <>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="can_order_ambulance"
                        checked={newUser.can_order_ambulance}
                        onChange={(e) => setNewUser({ ...newUser, can_order_ambulance: e.target.checked })}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label htmlFor="can_order_ambulance" className="text-sm text-gray-700">
                        {t('users.canOrderAmbulance')}
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="can_review_ambulance"
                        checked={newUser.can_review_ambulance}
                        onChange={(e) => setNewUser({ ...newUser, can_review_ambulance: e.target.checked })}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label htmlFor="can_review_ambulance" className="text-sm text-gray-700">
                        {t('users.canReviewAmbulance')}
                      </label>
                    </div>
                  </>
                )}
              </div>
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="send_invite_email"
                    checked={sendInviteEmail}
                    onChange={(e) => setSendInviteEmail(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="send_invite_email" className="text-sm text-gray-700">
                    {t('users.sendInviteEmail') || 'Lähetä kutsu sähköpostilla'}
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  {tc('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={inviteUserMutation.isPending}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {inviteUserMutation.isPending ? tc('common.saving') : t('users.invite')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cost Center Permissions Modal */}
      {permissionModalUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
              <h2 className="text-lg font-semibold">
                {t('users.manageCostCenterPermissions')} — {permissionModalUser.display_name || permissionModalUser.email}
              </h2>
              <button onClick={handleClosePermissionModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-3 border-b flex-shrink-0">
              <input
                type="text"
                value={permissionSearch}
                onChange={(e) => setPermissionSearch(e.target.value)}
                placeholder={t('costCenters.searchPlaceholder')}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
            </div>
            <div className="overflow-y-auto flex-1 px-2 py-2">
              {!costCentersData || !userPermsData ? (
                <div className="py-8 text-center text-gray-500 text-sm">{tc('common.loading')}</div>
              ) : costCentersData.data.filter(cc => !cc.is_deleted).length === 0 ? (
                <div className="py-8 text-center text-gray-500 text-sm">{t('costCenters.noResults')}</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-10"></th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('costCenters.code')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('costCenters.name')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('costCenters.permissions.validFrom')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('costCenters.permissions.validTo')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {costCentersData.data
                      .filter(cc => !cc.is_deleted)
                      .filter(cc => {
                        if (!permissionSearch) return true
                        const q = permissionSearch.toLowerCase()
                        return cc.code.toLowerCase().includes(q) || cc.name.toLowerCase().includes(q)
                      })
                      .map((cc) => {
                        const entry = permissionMap[cc.id]
                        if (!entry) return null
                        return (
                          <tr key={cc.id} className={`hover:bg-gray-50 ${entry.checked ? 'bg-primary-50' : ''}`}>
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={entry.checked}
                                onChange={(e) =>
                                  setPermissionMap((prev) => ({
                                    ...prev,
                                    [cc.id]: { ...prev[cc.id], checked: e.target.checked },
                                  }))
                                }
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                              />
                            </td>
                            <td className="px-3 py-2 font-mono text-gray-700">{cc.code}</td>
                            <td className="px-3 py-2 text-gray-700">{cc.name}</td>
                            <td className="px-3 py-2">
                              {entry.checked ? (
                                <input
                                  type="date"
                                  value={entry.valid_from}
                                  onChange={(e) =>
                                    setPermissionMap((prev) => ({
                                      ...prev,
                                      [cc.id]: { ...prev[cc.id], valid_from: e.target.value },
                                    }))
                                  }
                                  className="px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-primary-500"
                                />
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {entry.checked ? (
                                <input
                                  type="date"
                                  value={entry.valid_to}
                                  onChange={(e) =>
                                    setPermissionMap((prev) => ({
                                      ...prev,
                                      [cc.id]: { ...prev[cc.id], valid_to: e.target.value },
                                    }))
                                  }
                                  placeholder={t('costCenters.permissions.indefinite')}
                                  className="px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-primary-500"
                                />
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t flex-shrink-0">
              <button
                type="button"
                onClick={handleClosePermissionModal}
                className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
              >
                {tc('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSavePermissions}
                disabled={isSavingPermissions}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
              >
                {isSavingPermissions ? tc('common.saving') : tc('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {isEditModalOpen && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">{t('users.editUser')}</h2>
              <button onClick={() => setIsEditModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleUpdateUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.email')}
                </label>
                <input
                  type="email"
                  disabled
                  value={editingUser.email}
                  className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.phone')}
                </label>
                <input
                  type="tel"
                  value={editingUser.phone || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('users.role')} *
                </label>
                <select
                  required
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="user">{tc('roles.user')}</option>
                  <option value="organization_admin">{tc('roles.organization_admin')}</option>
                </select>
              </div>
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  {t('users.permissions')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit_can_review_invoices"
                    checked={editingUser.can_review_invoices}
                    onChange={(e) => setEditingUser({ ...editingUser, can_review_invoices: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="edit_can_review_invoices" className="text-sm text-gray-700">
                    {t('users.canReviewInvoices')}
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit_can_view_reports"
                    checked={editingUser.can_view_reports}
                    onChange={(e) => setEditingUser({ ...editingUser, can_view_reports: e.target.checked })}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="edit_can_view_reports" className="text-sm text-gray-700">
                    {t('users.canViewReports')}
                  </label>
                </div>
                {ambulanceEnabled && (
                  <>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="edit_can_order_ambulance"
                        checked={editingUser.can_order_ambulance}
                        onChange={(e) => setEditingUser({ ...editingUser, can_order_ambulance: e.target.checked })}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label htmlFor="edit_can_order_ambulance" className="text-sm text-gray-700">
                        {t('users.canOrderAmbulance')}
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="edit_can_review_ambulance"
                        checked={editingUser.can_review_ambulance}
                        onChange={(e) => setEditingUser({ ...editingUser, can_review_ambulance: e.target.checked })}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label htmlFor="edit_can_review_ambulance" className="text-sm text-gray-700">
                        {t('users.canReviewAmbulance')}
                      </label>
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
                >
                  {tc('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={updateUserMutation.isPending}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {updateUserMutation.isPending ? tc('common.saving') : tc('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
